import type { Collection } from '../collection'
import type * as models from '../models'

import { Maker } from './internals/maker'
import { Tracker } from './internals/tracker'

// TODO: maybe support different callbacks based on the entity source/type. E.g. something like the object below

// | {
//     readonly entity?: Callback<TTypeParams['entity']>
//     readonly creationEntity?: Callback<TTypeParams['createEntity']>
//     readonly updatedEntity?: Callback<TTypeParams['entity']>
//   },

export const create = withAliases({} as never).create
export namespace create {
  export type InitializationCallback<
    TTypeParams extends models.TypeParams.Constraint,
    TEntity = Partial<TTypeParams['entity']> | TTypeParams['createEntity'],
    TAliases = unknown,
  > = (this: Maker<TEntity, TEntity, TAliases>, entity: TEntity) => void
}

export function withAliases<
  const TAliases extends Record<string, string | ((...args: any) => string)>,
>(aliases: TAliases) {
  return {
    create: <TTypeParams extends models.TypeParams.Constraint>(
      cb: create.InitializationCallback<
        TTypeParams,
        Partial<TTypeParams['entity']> | TTypeParams['createEntity'],
        TAliases
      >,
    ): Manager<TTypeParams, TAliases> => ({
      bind: collection => {
        // Alias properties are written below
        const selectionTargetProps = {} as Manager.Bound<
          TTypeParams,
          TAliases
        >['selectionTarget']
        const selectionTargetBase = collection.name
        for (const k in aliases) {
          // FIXME: casting
          let prop: any
          // FIXME: this needs to be consistent with the actual selection
          // target that is used. Right now, it is just hard coded to be so,
          // but it should be implemented in a better shared way
          const aliasOrFactory = aliases[k]
          if (typeof aliasOrFactory === 'function') {
            // If this is a function that produces aliases, we prefix the
            // collection name to get the full target
            prop = (...args: readonly unknown[]) =>
              `${selectionTargetBase}±${aliasOrFactory(...args)}`
          } else {
            // If this is just an alias, we prefix thes
            // collection name to get the full target
            prop = `${selectionTargetBase}±${aliasOrFactory.toString()}`
          }
          selectionTargetProps[k as keyof typeof selectionTargetProps] = prop
        }
        return {
          selectionTarget: Object.assign(
            selectionTargetBase,
            selectionTargetProps,
          ),
        }
      },
      trackerConstructor: class extends Tracker<TTypeParams> {
        init() {
          cb.call(
            new Maker({
              aliases,
              extract: entity => entity,
              tracker: this,
            }),
            this.entity,
          )
        }
      },
    }),
  }
}

/**
 * @internal
 */
export interface Manager<
  TTypeParams extends models.TypeParams.Constraint,
  TAliases = unknown,
> {
  readonly trackerConstructor: Tracker.Constructor<TTypeParams>
  readonly bind: (
    collection: Collection<TTypeParams, any>,
  ) => Manager.Bound<TTypeParams, TAliases>
}
export namespace Manager {
  /**
   * TODO:
   */
  export interface Bound<
    TTypeParams extends models.TypeParams.Constraint,
    TAliases = unknown,
  > {
    readonly selectionTarget: string &
      ([TAliases] extends [never] ? string : TAliases)
  }
}
